/**
 * KISSED University · Student Portal Lite.
 * Route: /university/student
 *
 * Email-keyed lookup (pre-SSO). Surfaces six sections per the Fast Launch spec:
 *   Academic Overview · Courses · Billing · Progress · Certificates · Clinical Residency
 *
 * "Certificates" and "Clinical Residency" are placeholder sections showing
 * "Coming Soon" — the full LMS, certificate issuance, and residency scheduler
 * land in Phases U-3 / U-4 / U-5.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Search, GraduationCap, BookOpen, FileText,
  Wallet, Award, Stethoscope, CheckCircle2, Lock, CircleDashed, CreditCard,
  Calendar, Download, ClipboardCheck,
} from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { universityApi } from "@/lib/universityApi";
import { formatApiError } from "@/lib/formatError";

const TYPE_ICON = {
  certificate: BookOpen,
  residency: Stethoscope,
  degree: GraduationCap,
  continuing_education: FileText,
};
const STATUS_LABEL = {
  started: "Started",
  pending_payment: "Pending payment",
  pending: "Pending review",
  active: "Active",
  on_hold: "On hold",
  graduated: "Graduated",
  withdrawn: "Withdrawn",
  expelled: "Expelled",
};
const STATUS_TONE = {
  started: "bg-slate-100 text-slate-700",
  pending_payment: "bg-amber-100 text-amber-800",
  pending: "bg-purple-100 text-purple-800",
  active: "bg-emerald-100 text-emerald-800",
  on_hold: "bg-yellow-100 text-yellow-800",
  graduated: "bg-emerald-200 text-emerald-900",
  withdrawn: "bg-slate-200 text-slate-700",
  expelled: "bg-red-100 text-red-800",
};
const PLAN_LABEL = {
  pay_in_full: "Pay In Full",
  pay_per_course: "Pay Per Course",
  term_payment: "Term Payment",
  undecided: "Undecided",
};

const usd = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Phase U-Square deep-link: which enrollments are still owed tuition?
// Hide the Pay button for: graduated, withdrawn, expelled, or active w/ $0 balance.
const tuitionOwed = (enr) => {
  if (!enr) return false;
  if (["graduated", "withdrawn", "expelled"].includes(enr.status)) return false;
  if (enr.status === "pending_payment") return true;
  return Number(enr.tuition_balance_usd || 0) > 0;
};

function PayTuitionButton({ enrollment, variant = "primary", onPaid }) {
  const [busy, setBusy] = useState(false);
  if (!tuitionOwed(enrollment)) return null;

  const amount = Number(enrollment.tuition_balance_usd || 0);
  const label = amount > 0 ? `Pay ${usd(amount)} Now` : "Pay Tuition Now";

  const handleClick = async () => {
    setBusy(true);
    try {
      const r = await universityApi.enrollmentTuitionCheckout(enrollment.id);
      const url = r.data?.checkout_url;
      const src = r.data?.checkout_source;
      if (!url) {
        toast.error("Checkout link not available yet — please contact admissions.");
        return;
      }
      if (src === "paid") {
        toast.success("This enrollment is already paid in full.");
        if (onPaid) onPaid();
        return;
      }
      // Open Square checkout in a new tab so the portal state is preserved.
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Opening Square checkout in a new tab…");
    } catch (err) {
      toast.error(formatApiError(err, "Could not open checkout"));
    } finally {
      setBusy(false);
    }
  };

  const styles = variant === "compact"
    ? "h-8 px-3 text-xs rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white"
    : "h-10 px-4 text-sm rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={`inline-flex items-center gap-2 font-medium shadow-sm transition disabled:opacity-60 ${styles}`}
      data-testid={`sp-pay-tuition-${enrollment.id}`}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

export default function UniversityStudentPortal() {
  const [params, setParams] = useSearchParams();
  // Phase Auth-Student: require valid JWT in localStorage; lock the email
  // field to the token-bound email so portals can't be entered by typing
  // someone else's address.
  const navigate = useNavigate();
  const tokenEmail = (typeof window !== "undefined" ? localStorage.getItem("kcis_student_email") : null) || "";
  const token = (typeof window !== "undefined" ? localStorage.getItem("kcis_student_token") : null) || "";
  const paidFlagInUrl = params.get("paid") === "1";
  useEffect(() => {
    if (!token && !paidFlagInUrl) {
      // Allow the payment-return banner to load by enrollment_id without a token,
      // but anything else MUST authenticate.
      navigate(`/university/student/login`, { replace: true });
    }
  }, [token, paidFlagInUrl, navigate]);
  const [email, setEmail] = useState(tokenEmail || params.get("email") || "");
  const [loading, setLoading] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [progressById, setProgressById] = useState({});
  const [transcript, setTranscript] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [myEvents, setMyEvents] = useState([]);
  const [myCases, setMyCases] = useState([]);
  const [searched, setSearched] = useState(false);
  const [activeId, setActiveId] = useState(null);
  // Phase U-Square return: when Square redirects back to ?paid=1&enrollment_id=…
  const paidFlag = params.get("paid") === "1";
  const paidEnrollmentId = params.get("enrollment_id") || "";
  const [paymentReturn, setPaymentReturn] = useState(null);  // null | "pending" | "confirmed" | "timeout"
  // Phase Alumni: lookup alumni state once email is resolved
  const [alumni, setAlumni] = useState(null);    // { profile, membership }
  const [referralStats, setReferralStats] = useState(null);
  useEffect(() => {
    const e = email.trim();
    if (!e || !searched) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/university/alumni/profile/${encodeURIComponent(e)}`);
        if (!r.ok) { if (!cancelled) setAlumni(null); return; }
        const d = await r.json();
        if (!cancelled) setAlumni(d);
        try {
          const rs = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/university/alumni/referrals/${encodeURIComponent(e)}/stats`);
          if (rs.ok) { const ds = await rs.json(); if (!cancelled) setReferralStats(ds); }
        } catch (_) {}
      } catch (_) { if (!cancelled) setAlumni(null); }
    })();
    return () => { cancelled = true; };
  }, [email, searched, enrollments.length]);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter the email you used at enrollment.");
      return;
    }
    setLoading(true);
    try {
      const r = await universityApi.enrollmentByEmail(email.trim());
      const items = r.data?.items || [];
      setEnrollments(items);
      setSearched(true);
      // Fetch progress for each enrollment in parallel (best-effort)
      const progress = {};
      await Promise.all(items.map(async (enr) => {
        try {
          const pr = await universityApi.enrollmentProgress(enr.id);
          progress[enr.id] = pr.data;
        } catch (_) { /* swallow */ }
      }));
      setProgressById(progress);
      // Transcript (best-effort)
      try {
        const tr = await universityApi.studentTranscript(email.trim());
        setTranscript(tr.data);
      } catch (_) { setTranscript(null); }
      // Certificates (best-effort)
      try {
        const cr = await universityApi.lmsCertificates(email.trim());
        setCertificates(cr.data?.items || []);
      } catch (_) { setCertificates([]); }
      // CE / Residency event registrations
      try {
        const er = await universityApi.eventMyRegistrations(email.trim());
        setMyEvents(er.data?.items || []);
      } catch (_) { setMyEvents([]); }
      // Residency cases
      try {
        const rc = await universityApi.residencyCasesByEmail(email.trim());
        setMyCases(rc.data?.items || []);
      } catch (_) { setMyCases([]); }
      // auto-select active first
      const first = items.find((x) => x.status === "active") || items[0];
      setActiveId(first?.id || null);
    } catch (err) {
      toast.error(formatApiError(err, "Lookup failed"));
      setEnrollments([]);
      setProgressById({});
      setTranscript(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (email) {
      setTimeout(() => lookup(), 0);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase U-Square: Square redirected the student back. If we don't yet know
  // the email, fetch the enrollment by ID to resolve it, then auto-trigger
  // the standard lookup so the portal loads fully.
  useEffect(() => {
    if (!paidFlag || !paidEnrollmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await universityApi.enrollmentGet(paidEnrollmentId);
        const enr = r.data;
        if (!enr || cancelled) return;
        const resolvedEmail = (enr.student_email || "").trim();
        if (resolvedEmail && !email) {
          setEmail(resolvedEmail);
          setLoading(true);
          try {
            const list = await universityApi.enrollmentByEmail(resolvedEmail);
            const items = list.data?.items || [];
            setEnrollments(items);
            setSearched(true);
            const progress = {};
            await Promise.all(items.map(async (e) => {
              try { progress[e.id] = (await universityApi.enrollmentProgress(e.id)).data; }
              catch (_) { /* swallow */ }
            }));
            setProgressById(progress);
            try { setTranscript((await universityApi.studentTranscript(resolvedEmail)).data); }
            catch (_) { setTranscript(null); }
            try { setCertificates(((await universityApi.lmsCertificates(resolvedEmail)).data?.items) || []); } catch (_) { setCertificates([]); }
            try { setMyEvents(((await universityApi.eventMyRegistrations(resolvedEmail)).data?.items) || []); } catch (_) { setMyEvents([]); }
            try { setMyCases(((await universityApi.residencyCasesByEmail(resolvedEmail)).data?.items) || []); } catch (_) { setMyCases([]); }
            setActiveId(paidEnrollmentId);
          } catch (err) {
            toast.error(formatApiError(err, "Lookup failed"));
          } finally {
            setLoading(false);
          }
        } else if (!email) {
          setSearched(true);
        }
        // Initial banner state — refined by poller in PaymentReturnBanner.
        setPaymentReturn(enr.status === "active" || enr.status === "graduated" ? "confirmed" : "pending");
      } catch (err) {
        if (!cancelled) {
          toast.error("Could not load your enrollment. Please try again.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [paidFlag, paidEnrollmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Strip the `paid` URL params (but keep `email`) so a refresh doesn't replay
  // the success banner.
  const dismissPaymentReturn = () => {
    setPaymentReturn(null);
    const next = new URLSearchParams(params);
    next.delete("paid");
    next.delete("enrollment_id");
    setParams(next, { replace: true });
  };

  const active = useMemo(
    () => enrollments.find((e) => e.id === activeId) || null,
    [enrollments, activeId]
  );
  const activeProgress = active ? progressById[active.id] || null : null;

  return (
    <div data-testid="university-student-portal" className="min-h-screen bg-[#faf7f5]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/university"><img src={logo} alt="KISSED" className="h-11 w-auto drop-shadow-md" /></Link>
          <span className="font-display text-sm text-slate-700">Student Portal</span>
          <nav className="ml-auto flex items-center gap-3 text-sm">
            <Link to="/university/enroll" data-testid="link-enroll" className="text-purple-900 hover:underline">Open enrollment</Link>
            <span className="text-slate-300">·</span>
            <Link to="/university/status" data-testid="link-status" className="text-purple-900 hover:underline">Application status</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        {paidFlag && paidEnrollmentId && (
          <PaymentReturnBanner
            enrollmentId={paidEnrollmentId}
            state={paymentReturn}
            onState={setPaymentReturn}
            onDismiss={dismissPaymentReturn}
            onActivated={async () => {
              // Refresh portal data so the now-active enrollment + first
              // unlocked course surface immediately.
              if (email) await lookup();
            }}
          />
        )}
        <StudentResumeBanner />
        {/* Email gate */}
        <form onSubmit={lookup} className="rounded-2xl border border-slate-200 bg-white p-5 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <h1 className="font-display text-2xl text-slate-900">Welcome to your Student Portal</h1>
            <p className="text-sm text-slate-600 mt-1">Enter the email you used when you enrolled to view your academic overview, courses, billing, and progress.</p>
            <Label className="mt-3 block text-xs uppercase tracking-wide text-slate-500">Student email</Label>
            <Input data-testid="sp-email" type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} className="mt-1 max-w-md" />
          </div>
          <Button type="submit" disabled={loading} className="bg-purple-900 hover:bg-purple-800" data-testid="sp-lookup-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}Open portal
          </Button>
        </form>

        {searched && enrollments.length === 0 && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600 text-center" data-testid="sp-empty">
            We didn&apos;t find any enrollments for that email.
            <div className="mt-2">
              <Link to="/university/enroll" className="text-purple-900 hover:underline">Browse programs →</Link>
            </div>
          </div>
        )}

        {enrollments.length > 0 && (
          <StudentFeedbackPanel email={email} />
        )}

        {enrollments.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
            {/* Enrollment switcher */}
            <aside className="space-y-2" data-testid="sp-enrollment-switcher">
              <div className="text-[11px] uppercase tracking-wide text-slate-500 px-1">Your enrollments</div>
              {enrollments.map((e) => {
                const Icon = TYPE_ICON[e.program_type] || BookOpen;
                const selected = e.id === activeId;
                return (
                  <div key={e.id} className="space-y-1.5">
                    <button
                      type="button"
                      onClick={() => setActiveId(e.id)}
                      data-testid={`sp-enr-tab-${e.public_enrollment_id || e.id}`}
                      className={`w-full text-left rounded-xl border p-3 transition-all bg-white ${
                        selected ? "border-purple-700 ring-2 ring-purple-100 shadow" : "border-slate-200 hover:border-purple-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-purple-900" />
                        <div className="flex-1 text-sm font-medium text-slate-900 leading-tight line-clamp-2">{e.program_name}</div>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_TONE[e.status] || "bg-slate-100"}`}>
                          {STATUS_LABEL[e.status] || e.status}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">{e.public_enrollment_id || e.id.slice(0, 8)}</span>
                      </div>
                    </button>
                    {tuitionOwed(e) && (
                      <div className="px-1">
                        <PayTuitionButton enrollment={e} variant="compact" />
                      </div>
                    )}
                  </div>
                );
              })}
            </aside>

            {active && (
              <div className="space-y-6">
                {/* Academic Overview */}
                <Section title="Academic overview" icon={GraduationCap} testid="sp-section-overview">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <StatTile label="Name" value={active.student_name || "—"} />
                    <StatTile label="Program" value={active.program_name} />
                    <StatTile
                      label="Status"
                      value={STATUS_LABEL[active.status] || active.status}
                      tone={STATUS_TONE[active.status]}
                    />
                    <StatTile label="Start date" value={active.started_at ? new Date(active.started_at).toLocaleDateString() : "—"} />
                  </div>
                  {tuitionOwed(active) && (
                    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-3 flex flex-wrap items-center justify-between gap-3" data-testid="sp-overview-pay-cta">
                      <div className="text-sm text-amber-900">
                        <div className="font-medium">Tuition due: {usd(active.tuition_balance_usd)}</div>
                        <div className="text-xs text-amber-800/80 mt-0.5">
                          {active.status === "pending_payment"
                            ? "Pay now to activate full course access."
                            : "Settle your remaining balance to keep your enrollment in good standing."}
                        </div>
                      </div>
                      <PayTuitionButton enrollment={active} />
                    </div>
                  )}
                </Section>

                {/* Courses */}
                <Section title="Courses" icon={BookOpen} testid="sp-section-courses">
                  <CredentialHeader progress={activeProgress} />
                  <PrereqAlert progress={activeProgress} />
                  <CoursesPanel enrollment={active} progress={activeProgress} />
                  <ContinueCourseworkCTA enrollment={active} progress={activeProgress} />
                </Section>

                {/* Billing */}
                <Section title="Billing" icon={Wallet} testid="sp-section-billing">
                  <BillingPanel enrollment={active} />
                </Section>

                {/* Progress */}
                <Section title="Progress" icon={CircleDashed} testid="sp-section-progress">
                  <ProgressPanel progress={activeProgress} />
                </Section>

                {/* Credentials & Transcript */}
                <Section title="Credentials &amp; Transcript" icon={Award} testid="sp-section-certificates">
                  <CredentialsPanel transcript={transcript} certificates={certificates} />
                </Section>

                {/* Alumni Association */}
                <Section title="Alumni Association" icon={Award} testid="sp-section-alumni">
                  <AlumniPanel email={email} alumni={alumni} referralStats={referralStats} onRefresh={() => { setEnrollments((s) => [...s]); }} />
                </Section>

                {/* CE Events */}
                <Section title="My CE &amp; Events" icon={Calendar} testid="sp-section-events">
                  <EventsPanel events={myEvents} />
                </Section>

                {/* Clinical Residency */}
                <Section title="Clinical residency" icon={Stethoscope} testid="sp-section-residency">
                  <ResidencyPanel events={myEvents} cases={myCases} />
                </Section>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function CredentialHeader({ progress }) {
  if (!progress?.credential) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="sp-credential-header">
      <Award className="w-4 h-4 text-purple-900" />
      <span className="text-xs uppercase tracking-wide text-slate-500">Credential on completion</span>
      <span className="text-sm font-medium text-purple-900">{progress.credential}</span>
    </div>
  );
}

function PrereqAlert({ progress }) {
  const prereqs = progress?.prerequisite_status || [];
  if (prereqs.length === 0) return null;
  const unmet = prereqs.filter((p) => !p.satisfied);
  if (unmet.length === 0) {
    return (
      <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="sp-prereq-ok">
        <span className="font-medium">All prerequisite credentials earned.</span>
      </div>
    );
  }
  return (
    <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="sp-prereq-alert">
      <div className="font-medium text-amber-900">Prerequisite pathway incomplete</div>
      <div className="mt-1">
        This program requires completion of:
        <ul className="list-disc list-inside mt-1">
          {unmet.map((p) => (
            <li key={p.level_key}><span className="font-mono text-[11px]">{p.level_key}</span></li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CredentialsPanel({ transcript, certificates = [] }) {
  if (!transcript) {
    return (
      <div className="text-sm text-slate-600">No transcript available yet.</div>
    );
  }
  const earned = transcript.awarded_credentials || [];
  const enrollments = transcript.enrollments || [];
  const studentEmail = transcript.student_email || "";
  // Active (not graduated) enrollments are eligible for a final exam CTA
  const examable = enrollments.filter((e) => e.status !== "graduated" && e.status !== "withdrawn" && e.status !== "expelled" && e.status !== "cancelled");
  return (
    <div className="space-y-4" data-testid="sp-credentials-panel">
      {/* Final Exam CTAs (one per non-graduated enrollment) */}
      {examable.length > 0 && (
        <div className="space-y-2">
          {examable.map((e) => {
            const enrId = e.enrollment_id || e.id;
            return (
              <a
                key={enrId}
                href={`/university/final-exam/${enrId}`}
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm flex items-center justify-between gap-3 hover:border-amber-400"
                data-testid={`sp-final-exam-cta-${enrId}`}
              >
                <div>
                  <div className="font-medium text-amber-900">Program Final Exam — {e.program_name || e.program_key}</div>
                  <div className="text-[11px] text-amber-800">Required for graduation. View eligibility &amp; start exam →</div>
                </div>
                <span className="text-amber-900 font-medium">Open →</span>
              </a>
            );
          })}
        </div>
      )}
      {/* Transcript PDF download */}
      {studentEmail && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/40 p-3 text-sm flex items-center justify-between gap-3" data-testid="sp-transcript-pdf-row">
          <div>
            <div className="font-medium text-slate-900">Official Academic Transcript</div>
            <div className="text-[11px] text-slate-600">Printable PDF · all enrollments, credentials, and clock hours.</div>
          </div>
          <a
            href={universityApi.transcriptPdfUrl(studentEmail)}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-xs font-medium px-3 h-9 inline-flex items-center gap-1"
            data-testid="sp-transcript-pdf-download"
          >
            <Download className="w-3 h-3" />Download Transcript (PDF)
          </a>
        </div>
      )}
      {/* Downloadable certificates */}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">My certificates</div>
        {certificates.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            No certificates yet — they&apos;ll appear here as you complete programs.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {certificates.map((c) => (
              <li key={c.id} className="rounded-xl border border-purple-200 bg-white p-3 text-sm flex items-center justify-between gap-3" data-testid={`sp-certificate-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{c.credential}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{c.public_id} · {c.issued_at?.slice(0,10)}</div>
                </div>
                <a className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-xs font-medium px-3 h-9 inline-flex items-center gap-1" href={universityApi.lmsCertificatePdfUrl(c.id)} target="_blank" rel="noreferrer" data-testid={`sp-cert-download-${c.id}`}>
                  <Download className="w-3 h-3" />PDF
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Credentials earned</div>
        {earned.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            No credentials earned yet. Complete every course in your program to earn the credential.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {earned.map((c, i) => (
              <li key={i} className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm" data-testid={`sp-credential-${c.level_key}`}>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-emerald-700" />
                  <span className="font-medium text-emerald-900">{c.credential}</span>
                </div>
                <div className="mt-1 text-[11px] text-emerald-800/80">
                  {c.program_key}
                </div>
                {c.upgrade_from && (
                  <div className="mt-1 text-[11px] text-purple-900">
                    Upgraded from: <span className="font-medium">{c.upgrade_from}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {transcript.highest_trichology_credential && (
        <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-3 text-sm text-slate-700" data-testid="sp-highest-credential">
          <span className="text-[11px] uppercase tracking-wide text-purple-900 font-semibold mr-2">Highest tier</span>
          <span className="text-purple-900 font-medium">{transcript.highest_trichology_credential}</span>
        </div>
      )}
      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Academic record</div>
        <div className="rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="sp-transcript-table">
          {enrollments.map((e) => (
            <div key={e.enrollment_id} className="px-3 py-2 text-sm flex items-center justify-between">
              <div>
                <div className="font-medium text-slate-900">{e.program_name}</div>
                <div className="text-[11px] text-slate-500 font-mono">
                  {e.public_enrollment_id || e.enrollment_id.slice(0,8)} · {e.credential || "—"}
                </div>
              </div>
              <div className="text-right">
                <div className="text-slate-900 font-medium">{e.progress_percent}%</div>
                <div className="text-[11px] text-slate-500">{e.courses_completed}/{e.courses_total} courses</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PaymentReturnBanner({ enrollmentId, state, onState, onDismiss, onActivated }) {
  const [enr, setEnr] = useState(null);
  const [progress, setProgress] = useState(null);
  const [attempts, setAttempts] = useState(0);

  // Poll the enrollment + progress every 2s for up to 30s (15 attempts).
  useEffect(() => {
    if (!enrollmentId) return;
    let cancelled = false;
    let stopped = false;
    let timer = null;

    const tick = async () => {
      if (cancelled || stopped) return;
      try {
        const [eRes, pRes] = await Promise.all([
          universityApi.enrollmentGet(enrollmentId),
          universityApi.enrollmentProgress(enrollmentId).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const e = eRes.data;
        setEnr(e);
        setProgress(pRes.data);
        if (e && (e.status === "active" || e.status === "graduated")) {
          stopped = true;
          if (state !== "confirmed") {
            onState("confirmed");
            if (onActivated) onActivated();
          }
          return;
        }
        // still pending
        setAttempts((n) => {
          const next = n + 1;
          if (next >= 15) {
            onState("timeout");
            stopped = true;
          }
          return next;
        });
      } catch (_) { /* swallow */ }
      if (!stopped) timer = setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [enrollmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!enr) {
    return (
      <div className="rounded-2xl border border-purple-200 bg-purple-50 p-5 text-sm text-purple-900 flex items-center gap-3" data-testid="sp-payment-return-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your enrollment…
      </div>
    );
  }

  const isConfirmed = state === "confirmed" || enr.status === "active" || enr.status === "graduated";
  const isTimeout = state === "timeout";
  const firstUnlocked = (progress?.courses || []).find((c) => c.unlocked && !c.completed);

  return (
    <div
      className={`rounded-2xl border p-6 shadow-sm ${
        isConfirmed
          ? "border-emerald-300 bg-emerald-50"
          : isTimeout
          ? "border-amber-300 bg-amber-50"
          : "border-purple-200 bg-purple-50"
      }`}
      data-testid={`sp-payment-return-${isConfirmed ? "success" : isTimeout ? "timeout" : "pending"}`}
    >
      <div className="flex items-start gap-4">
        <div className={`mt-1 w-10 h-10 rounded-full flex items-center justify-center ${
          isConfirmed ? "bg-emerald-100 text-emerald-700"
          : isTimeout ? "bg-amber-100 text-amber-700"
          : "bg-purple-100 text-purple-700"
        }`}>
          {isConfirmed ? <CheckCircle2 className="w-5 h-5" />
            : isTimeout ? <Wallet className="w-5 h-5" />
            : <Loader2 className="w-5 h-5 animate-spin" />}
        </div>
        <div className="flex-1 min-w-0">
          {isConfirmed ? (
            <>
              <div className="font-display text-xl text-emerald-900">Your enrollment payment was received.</div>
              <p className="mt-1 text-sm text-emerald-900/85">
                Welcome to KISSED University. Your student portal has been updated and your first course is now available.
              </p>
            </>
          ) : isTimeout ? (
            <>
              <div className="font-display text-xl text-amber-900">Still confirming your payment…</div>
              <p className="mt-1 text-sm text-amber-900/85">
                Square is taking longer than usual to confirm. Reload this page in a moment — your enrollment will activate automatically.
              </p>
            </>
          ) : (
            <>
              <div className="font-display text-xl text-purple-900">Your payment is being confirmed</div>
              <p className="mt-1 text-sm text-purple-900/85">
                This usually takes a few moments. You can stay on this page — we&apos;ll auto-update the second Square confirms.
              </p>
            </>
          )}

          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <StatTile label="Program" value={enr.program_name || "—"} />
            <StatTile label="Amount paid" value={usd(enr.tuition_paid_usd)} />
            <StatTile
              label="Status"
              value={STATUS_LABEL[enr.status] || enr.status}
              tone={STATUS_TONE[enr.status]}
            />
            <StatTile label="Remaining balance" value={usd(enr.tuition_balance_usd)} />
          </div>

          {isConfirmed && firstUnlocked && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-white p-4" data-testid="sp-payment-return-first-course">
              <div className="text-[11px] uppercase tracking-wide text-emerald-800">First unlocked course</div>
              <div className="mt-0.5 text-sm font-medium text-slate-900">
                <span className="font-mono text-[11px] text-slate-500 mr-2">{firstUnlocked.code}</span>
                {firstUnlocked.name}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Link
                  to={`/university/courses/${enrollmentId}/${encodeURIComponent(firstUnlocked.code)}`}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium px-4 h-10"
                  data-testid="sp-payment-return-continue-btn"
                >
                  <BookOpen className="w-4 h-4" />Continue Coursework
                </Link>
                <span className="text-xs text-slate-500">Next step: open your first course to begin.</span>
              </div>
            </div>
          )}

          {!isConfirmed && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-xl bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-4 h-10"
                data-testid="sp-payment-return-reload-btn"
              >
                <Loader2 className="w-4 h-4" />Reload portal
              </button>
              <button
                type="button"
                onClick={onDismiss}
                className="text-xs text-slate-600 hover:text-slate-900 underline"
                data-testid="sp-payment-return-dismiss-btn"
              >
                Dismiss for now
              </button>
            </div>
          )}
          {isConfirmed && !firstUnlocked && (
            <button
              type="button"
              onClick={onDismiss}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium px-4 h-10"
              data-testid="sp-payment-return-continue-btn"
            >
              <BookOpen className="w-4 h-4" />Go to my portal
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ContinueCourseworkCTA({ enrollment, progress }) {
  const [link, setLink] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!enrollment || !progress) return setLink(null);
      const nextCourse = (progress.courses || []).find((c) => c.unlocked && !c.completed);
      if (!nextCourse) return setLink(null);
      try {
        const r = await universityApi.lmsModules(nextCourse.code, enrollment.id);
        const mods = r.data?.modules || [];
        for (const m of mods) {
          const l = (m.lessons || []).find((x) => !x.completed);
          if (l) { if (!cancelled) setLink({ courseCode: nextCourse.code, courseName: nextCourse.name, lessonId: l.id, lessonTitle: l.title }); return; }
        }
        // All lessons done — fall back to course page
        if (!cancelled) setLink({ courseCode: nextCourse.code, courseName: nextCourse.name });
      } catch (_) {
        if (!cancelled) setLink({ courseCode: nextCourse.code, courseName: nextCourse.name });
      }
    })();
    return () => { cancelled = true; };
  }, [enrollment?.id, progress?.progress_percent]);

  if (!link) return null;
  const target = link.lessonId
    ? `/university/lessons/${link.lessonId}?enrollment_id=${enrollment.id}`
    : `/university/courses/${enrollment.id}/${encodeURIComponent(link.courseCode)}`;
  return (
    <Link to={target} className="mt-3 inline-flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition w-full p-4" data-testid="sp-continue-coursework-cta">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-emerald-800">Continue Coursework</div>
        <div className="font-medium text-emerald-900 mt-0.5">
          <span className="font-mono text-[11px] mr-2">{link.courseCode}</span>
          {link.lessonTitle || link.courseName}
        </div>
      </div>
      <span className="text-sm font-medium text-emerald-900">Open lesson →</span>
    </Link>
  );
}

function EventsPanel({ events }) {
  if (!events || events.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        No CE or event registrations yet. <Link to="/university/ce-calendar" className="text-purple-900 hover:underline">Browse the CE Calendar →</Link>
      </div>
    );
  }
  const cePart = events.filter((r) => r.event_type === "continuing_education");
  return (
    <div className="space-y-2" data-testid="sp-events-panel">
      {cePart.length === 0 && <div className="text-xs text-slate-500">No CE registrations.</div>}
      {cePart.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`sp-event-${r.id}`}>
          <div className="min-w-0">
            <div className="font-medium text-slate-900 text-sm truncate">{r.event_title}</div>
            <div className="text-[11px] text-slate-500">{r.event?.starts_at ? new Date(r.event.starts_at).toLocaleString() : ""}</div>
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "paid" ? "bg-emerald-100 text-emerald-800" : r.status === "attended" ? "bg-purple-100 text-purple-800" : r.status === "waitlist" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{r.status}</span>
        </div>
      ))}
      <div className="text-xs">
        <Link to="/university/ce-calendar" className="text-purple-900 hover:underline">Browse more CE →</Link>
      </div>
    </div>
  );
}

function ResidencyPanel({ events, cases }) {
  const residencyRegs = (events || []).filter((r) => r.event_type === "clinical_residency");
  if (residencyRegs.length === 0 && (cases || []).length === 0) {
    return (
      <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-4 text-sm text-slate-700">
        <div className="font-medium text-slate-900">Schedule your residency</div>
        <p className="mt-1 text-xs text-slate-600">
          Choose between the in-person intensive (with clinical kit) or the 100% online case-based residency.
        </p>
        <Link to="/university/residency" className="mt-2 inline-block text-purple-900 hover:underline text-sm">View available sessions →</Link>
      </div>
    );
  }
  const totalHours = residencyRegs.reduce((sum, r) => sum + (r.mentorship_hours_logged || 0) + (r.event?.residency_hours || 0), 0);
  return (
    <div className="space-y-3" data-testid="sp-residency-panel">
      {residencyRegs.map((r) => (
        <div key={r.id} className="rounded-xl border border-slate-200 p-3" data-testid={`sp-residency-${r.id}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-slate-900 text-sm">{r.event_title}</div>
              <div className="text-[11px] text-slate-500">{r.event?.starts_at ? new Date(r.event.starts_at).toLocaleString() : ""} · Track: {r.event?.track || "—"}</div>
            </div>
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.status === "attended" ? "bg-purple-100 text-purple-800" : r.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{r.status}</span>
          </div>
          {r.mentorship_hours_logged > 0 && (
            <div className="mt-2 text-xs text-slate-600">Mentorship hours logged: <strong>{r.mentorship_hours_logged}</strong></div>
          )}
        </div>
      ))}
      {(cases || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Assigned cases</div>
          <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
            {cases.map((c) => (
              <div key={c.id} className="px-3 py-2 text-sm flex items-center justify-between" data-testid={`sp-case-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{c.case_title}</div>
                  <div className="text-[11px] text-slate-500">{c.evaluator_email ? `Evaluator: ${c.evaluator_email}` : ""}</div>
                </div>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AlumniPanel({ email, alumni, referralStats, onRefresh }) {
  const [busy, setBusy] = useState("");
  if (!alumni || !alumni.profile) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="sp-alumni-empty">
        Complete a program to become a KISSED Alumni. Your alumni profile is created automatically the moment you graduate.
      </div>
    );
  }
  const profile = alumni.profile;
  const membership = alumni.membership;
  const isActive = membership && (membership.lifetime || membership.status === "active");
  const isLifetime = membership && membership.lifetime;

  const purchase = async (tier) => {
    setBusy(tier);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/university/alumni/membership/${encodeURIComponent(email)}/purchase`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tier }),
      });
      const d = await r.json();
      if (d.checkout_url) {
        window.open(d.checkout_url, "_blank", "noopener,noreferrer");
        toast.success("Opening Square checkout for alumni membership…");
      } else {
        toast.error("Checkout link unavailable. Please contact alumni@kissedbycvon.com");
      }
      onRefresh();
    } catch (_) { toast.error("Purchase failed"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="sp-alumni-panel">
      <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-purple-900">Alumni Status</div>
            <div className="font-display text-lg text-slate-900 mt-0.5">
              {isLifetime ? "Lifetime Alumni" : isActive ? `Active Alumni · ${membership.tier}` : "Inactive — renewal required"}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Graduation year: <strong>{profile.graduation_year || "—"}</strong>{" "}
              {membership?.expires_at && !isLifetime && <span>· Renews {membership.expires_at.slice(0,10)}</span>}
            </div>
            {(profile.credentials_held || []).length > 0 && (
              <div className="mt-1 text-xs text-slate-600">Credentials: {profile.credentials_held.join(" · ")}</div>
            )}
          </div>
          <Link to="/university/alumni" className="text-xs text-purple-900 hover:underline">Public directory →</Link>
        </div>
      </div>

      {!isLifetime && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Membership tiers</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <TierCard tier="standard" price="$99/year" benefits={["Alumni Directory","Alumni Badge","10% CE Discount","Newsletter","Referral Program"]} onClick={() => purchase("standard")} busy={busy === "standard"} current={isActive && membership?.tier === "standard"} />
            <TierCard tier="professional" price="$199/year" benefits={["Featured Listing","Business Spotlight","Referral Analytics","Enhanced Profile","Priority Registration"]} onClick={() => purchase("professional")} busy={busy === "professional"} current={isActive && membership?.tier === "professional"} />
            <TierCard tier="lifetime" price="$999 once" benefits={["Lifetime Status","No Renewals","Permanent Listing","All Benefits"]} onClick={() => purchase("lifetime")} busy={busy === "lifetime"} highlight />
          </div>
        </div>
      )}

      {referralStats && (
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="sp-referral-stats">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">Referral program</div>
              <div className="text-sm text-slate-900 mt-0.5">Your link: <span className="font-mono text-purple-900">{window.location.origin}{referralStats.referral_link}</span></div>
            </div>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${referralStats.referral_link}`); toast.success("Copied"); }}
              className="text-xs rounded-xl border border-purple-300 text-purple-900 px-3 h-8"
              data-testid="sp-referral-copy-btn"
            >Copy link</button>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Tile label="Leads" value={referralStats.leads || 0} />
            <Tile label="Applications" value={referralStats.applications || 0} />
            <Tile label="Enrollments" value={referralStats.enrollments || 0} />
            <Tile label="Tuition generated" value={`$${(referralStats.tuition_generated_usd || 0).toLocaleString()}`} />
          </div>
        </div>
      )}
    </div>
  );
}

function TierCard({ tier, price, benefits, onClick, busy, current, highlight }) {
  return (
    <div className={`rounded-xl border p-3 ${highlight ? "border-amber-300 bg-amber-50/60" : current ? "border-emerald-300 bg-emerald-50/60" : "border-slate-200 bg-white"}`} data-testid={`sp-tier-${tier}`}>
      <div className="font-display text-base text-slate-900 capitalize">{tier}</div>
      <div className="text-sm text-slate-700 mt-0.5">{price}</div>
      <ul className="mt-2 space-y-0.5 text-[11px] text-slate-600">
        {benefits.map((b, i) => <li key={i}>• {b}</li>)}
      </ul>
      <button
        type="button" onClick={onClick} disabled={busy || current}
        className={`mt-3 w-full rounded-xl h-9 text-xs font-medium ${current ? "bg-emerald-700 text-white" : "bg-purple-900 hover:bg-purple-800 text-white"} disabled:opacity-60`}
        data-testid={`sp-tier-buy-${tier}`}
      >
        {current ? "Current" : busy ? "Opening…" : `Purchase ${tier}`}
      </button>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-slate-900 font-medium">{value}</div>
    </div>
  );
}

function StudentResumeBanner() {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    try { setDraft(JSON.parse(localStorage.getItem("kcis.enroll.draft.v1") || "null")); }
    catch { setDraft(null); }
  }, []);
  if (!draft?.selected) return null;
  return (
    <div className="rounded-2xl border border-purple-200 bg-purple-50/70 p-4 text-sm text-purple-900 flex items-center justify-between" data-testid="student-resume-banner">
      <div>
        <div className="font-medium">You have an enrollment in progress</div>
        <div className="text-xs mt-0.5">{draft.selected}</div>
      </div>
      <Link to="/university/enroll" className="rounded-lg bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium px-3 py-1.5" data-testid="student-resume-btn">
        Resume Enrollment
      </Link>
    </div>
  );
}

function Section({ title, icon: Icon, testid, children }) {
  return (
    <section data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5">
      <h2 className="font-display text-base text-slate-900 flex items-center gap-2 mb-3">
        {Icon ? <Icon className="w-4 h-4 text-purple-900" /> : null}{title}
      </h2>
      {children}
    </section>
  );
}

function StatTile({ label, value, tone }) {
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      {tone ? (
        <div className={`mt-0.5 inline-block px-2 py-0.5 rounded-full text-xs ${tone}`}>{value}</div>
      ) : (
        <div className="mt-0.5 text-sm font-medium text-slate-900 leading-tight">{value}</div>
      )}
    </div>
  );
}

function CoursesPanel({ enrollment, progress }) {
  const courses = progress?.courses || [];
  if (courses.length === 0) {
    return (
      <div className="text-sm text-slate-600">
        Course access for this program is being configured. You can already see your billing and overall progress above —
        course-level access opens here as soon as your admin publishes the course list.
        {enrollment.payment_plan === "pay_per_course" && (enrollment.purchased_courses || []).length > 0 && (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Your unlocked courses</div>
            <ul className="space-y-1">
              {(enrollment.purchased_courses || []).map((c, i) => (
                <li key={i} className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-1.5 inline-block mr-2">
                  <span className="font-mono text-xs text-slate-500">{c.course_code}</span> {c.course_name || ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
  const purchased = courses.filter((c) => c.purchased && !c.locked && !c.completed && !c.credited);
  const available = courses.filter((c) => c.unlocked && !c.completed && !c.credited);
  const credited  = courses.filter((c) => c.credited);
  const locked    = courses.filter((c) => c.locked);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      <CourseBucket title="Purchased / Unlocked" courses={available} icon={CheckCircle2} tone="text-emerald-700" testid="sp-bucket-available" />
      <CourseBucket title="Completed" courses={courses.filter((c) => c.completed)} icon={CheckCircle2} tone="text-emerald-900" testid="sp-bucket-completed" />
      <CourseBucket title="Credited (prior coursework)" courses={credited} icon={Award} tone="text-purple-700" testid="sp-bucket-credited" />
      <CourseBucket title="Locked" courses={locked} icon={Lock} tone="text-slate-500" testid="sp-bucket-locked" />
    </div>
  );
}

function CourseBucket({ title, courses, icon: Icon, tone, testid }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
        {Icon ? <Icon className={`w-3.5 h-3.5 ${tone}`} /> : null}{title} · {courses.length}
      </div>
      {courses.length === 0 ? (
        <div className="text-xs text-slate-400 italic">None</div>
      ) : (
        <ul className="space-y-1.5">
          {courses.map((c) => (
            <li key={c.code} className="text-xs text-slate-700">
              <span className="font-mono text-[11px] text-slate-500">{c.code}</span>
              {" "}{c.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BillingPanel({ enrollment: e }) {
  const isTerm = e.payment_plan === "term_payment" && e.term_count > 0;
  const termsPaid = (e.purchased_courses || []).filter((p) => (p.course_code || "").toUpperCase().startsWith("TERM")).length;
  const termsRemaining = Math.max(0, e.term_count - termsPaid);
  const owed = tuitionOwed(e);
  const nextDue = (() => {
    if (!owed) return null;
    if (isTerm) return `${usd(e.per_term_amount_usd)} (Term ${termsPaid + 1} of ${e.term_count})`;
    if (e.payment_plan === "pay_per_course") return "Per course at checkout";
    return usd(e.tuition_balance_usd);
  })();
  return (
    <div className="space-y-3" data-testid="sp-billing-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Tuition amount" value={usd(e.tuition_total_usd)} />
        <StatTile label="Amount paid" value={usd(e.tuition_paid_usd)} />
        <StatTile label="Remaining balance" value={usd(e.tuition_balance_usd)} />
        <StatTile label="Payment option" value={PLAN_LABEL[e.payment_plan] || e.payment_plan} />
      </div>
      {owed ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="sp-billing-pay-cta">
          <div className="text-sm text-emerald-900">
            <div className="text-[11px] uppercase tracking-wide text-emerald-800">Next payment due</div>
            <div className="font-medium mt-0.5">{nextDue}</div>
            <div className="text-xs text-emerald-800/80 mt-0.5">Secure checkout via Square. Activation is automatic.</div>
          </div>
          <PayTuitionButton enrollment={e} />
        </div>
      ) : (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900 flex items-center gap-2" data-testid="sp-billing-paid">
          <CheckCircle2 className="w-4 h-4 text-emerald-700" />
          <span className="font-medium">Tuition paid in full</span>
          <span className="text-emerald-800/80">— no outstanding balance.</span>
        </div>
      )}
      {isTerm && (
        <div className="rounded-xl border border-purple-100 bg-purple-50/60 p-3 text-xs text-slate-700" data-testid="sp-billing-terms">
          <div className="font-medium text-slate-900 text-sm">Term schedule</div>
          <div className="mt-1">
            {e.term_count} terms × {usd(e.per_term_amount_usd)} · {termsPaid} paid · {termsRemaining} remaining
          </div>
        </div>
      )}
      {(e.purchased_courses || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1.5">Payment history</div>
          <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
            {(e.purchased_courses || []).map((c, i) => (
              <div key={i} className="px-3 py-2 text-sm flex items-center justify-between" data-testid={`sp-payment-${e.id}-${i}`}>
                <div>
                  <div className="font-medium text-slate-900">{c.course_name || c.course_code}</div>
                  <div className="text-[11px] text-slate-500 font-mono">
                    {c.course_code}{c.payment_reference ? ` · ${c.payment_reference}` : ""}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-slate-900 font-medium">{usd(c.amount_paid_usd)}</div>
                  <div className="text-[11px] text-slate-500">
                    {c.purchased_at ? new Date(c.purchased_at).toLocaleDateString() : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressPanel({ progress }) {
  const total = progress?.courses_total ?? 0;
  const done  = progress?.courses_completed ?? 0;
  const rem   = progress?.courses_remaining ?? 0;
  const pct   = progress?.progress_percent ?? 0;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <div className="text-slate-700">Program completion</div>
        <div className="text-slate-900 font-medium" data-testid="sp-progress-pct">{pct}%</div>
      </div>
      <div className="mt-2 w-full bg-slate-100 rounded-full h-2 overflow-hidden">
        <div className="h-full bg-purple-700 transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <StatTile label="Courses total"     value={total === 0 ? "—" : total} />
        <StatTile label="Courses completed" value={done} />
        <StatTile label="Courses remaining" value={rem} />
      </div>
      {total === 0 && (
        <p className="text-xs text-slate-500 mt-3">
          Course-level progress will populate as soon as your admin publishes the course list for this program.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Student-facing feedback panel — surfaces published instructor feedback +
// grades. NEVER shows draft or instructor_private_notes (server enforces).
// ─────────────────────────────────────────────────────────────────────────────

function StudentFeedbackPanel({ email }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});      // expanded map by feedback id

  useEffect(() => {
    if (!email) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await universityApi.studentPublishedFeedback(email);
        if (!cancelled) setItems(r.data?.items || []);
      } catch (_) { if (!cancelled) setItems([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [email]);

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section
      className="rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50/60 to-white p-5"
      data-testid="sp-feedback-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-purple-900">Feedback & Grades</div>
          <div className="font-display text-lg text-slate-900">
            {items.length} item{items.length === 1 ? "" : "s"} from your instructors
          </div>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(({ feedback: f, grade: g, submission: s }) => {
          const isOpen = !!open[f.id];
          return (
            <div
              key={f.id}
              className={`rounded-xl border bg-white transition ${isOpen ? "border-purple-300 shadow-sm" : "border-slate-200 hover:border-purple-300"}`}
              data-testid={`sp-feedback-card-${f.id}`}
            >
              <button
                onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                className="w-full text-left p-3"
              >
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-0.5">
                  {f.course_code} · {s?.kind || "assignment"}
                </div>
                <div className="text-sm font-medium text-slate-900 truncate">{s?.title || "(untitled submission)"}</div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  {g ? (
                    <span className={`px-2 py-0.5 rounded-full ${g.passed ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                      {g.score}/{g.max_score} · {g.passed ? "PASS" : "FAIL"}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">No grade yet</span>
                  )}
                  <span className="text-slate-400">{f.approved_at ? new Date(f.approved_at).toLocaleDateString() : ""}</span>
                </div>
                {f.revision_request && (
                  <div className="mt-2 text-[11px] inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Revision requested
                  </div>
                )}
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 p-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid={`sp-feedback-detail-${f.id}`}>
                  {f.student_visible_text || "(no feedback text)"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
